import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, FileText, Loader2 } from "lucide-react";
import { api, errMsg, inr, num } from "@/lib/api";
import { Card } from "@/nexfin/Dashboard";

const PERIODS = [{ id: "daily", label: "Daily", days: 30 }, { id: "monthly", label: "Monthly", days: 365 }];
const MODES = ["ALL", "PAPER", "LIVE"];
const toneCls = (v) => (v > 0 ? "text-bull" : v < 0 ? "text-bear" : "text-slate-400");

export default function Reports() {
  const [period, setPeriod] = useState("daily");
  const [mode, setMode] = useState("ALL");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/nexfin/reports", { params: { period, mode, days } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  }, [period, mode, days]);
  useEffect(() => { load(); }, [load]);

  const pickPeriod = (p) => { setPeriod(p.id); setDays(p.days); };

  const csv = () => {
    if (!data?.rows?.length) return;
    const cols = ["label", "trades", "wins", "losses", "win_rate", "qty", "pnl", "cum_pnl"];
    const head = ["Period", "Trades", "Wins", "Losses", "Win %", "Qty", "Net P&L", "Cumulative"];
    const rows = [head.join(","), ...data.rows.map((x) => cols.map((c) => `"${x[c]}"`).join(","))];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    a.download = `nexfin_${period}_report.csv`; a.click();
  };

  const pdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/nexfin/reports/pdf", { params: { period, mode, days }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `nexfin_${period}_report.pdf`; a.click();
      toast.success("PDF exported");
    } catch (e) { toast.error(errMsg(e)); }
    setPdfBusy(false);
  };

  const s = data?.summary;
  const periodWord = period === "monthly" ? "months" : "days";
  const chartData = (data?.rows || []).map((x) => ({ ...x, name: period === "monthly" ? x.label.replace(" ", "\n") : x.label.slice(0, 6) }));

  return (
    <div className="space-y-3 rise" data-testid="reports-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-white tracking-tight">P&amp;L Reports</h1>
        <div className="flex rounded-sm overflow-hidden border border-[#163056]" data-testid="report-period-toggle">
          {PERIODS.map((p) => (
            <button key={p.id} data-testid={`report-period-${p.id}`} onClick={() => pickPeriod(p)}
              className={`h-8 px-3 text-xs font-semibold ${period === p.id ? "bg-[#22A34A] text-white" : "bg-[#0B1F3A] text-slate-400 hover:text-white"}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex rounded-sm overflow-hidden border border-[#163056]" data-testid="report-mode-toggle">
          {MODES.map((m) => (
            <button key={m} data-testid={`report-mode-${m.toLowerCase()}`} onClick={() => setMode(m)}
              className={`h-8 px-3 text-xs font-semibold ${mode === m ? "bg-[#102A4A] text-[#22A34A]" : "bg-[#0B1F3A] text-slate-400 hover:text-white"}`}>{m}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="report-csv-button" disabled={!data?.rows?.length} onClick={csv} className="btn-ghost h-8 disabled:opacity-40"><Download className="w-3.5 h-3.5" /> CSV</button>
          <button data-testid="report-pdf-button" disabled={pdfBusy} onClick={pdf} className="btn-primary h-8">{pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF</button>
        </div>
      </div>

      {busy && !data ? <div className="text-slate-500 text-sm p-4">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary">
            <Card label="Net P&L" value={inr(s?.net_pnl)} sub={`over ${s?.periods || 0} ${periodWord}`} cls={`${toneCls(s?.net_pnl)} text-xl`} tid="report-net" />
            <Card label="Win rate" value={`${s?.win_rate ?? 0}%`} sub={`${s?.wins ?? 0}W / ${s?.losses ?? 0}L · ${s?.total_trades ?? 0} trades`} cls="text-white text-xl" tid="report-winrate" />
            <Card label={`Best ${periodWord.slice(0, -1)}`} value={s?.best ? inr(s.best.pnl) : "—"} sub={s?.best?.label || "—"} cls="text-bull text-xl" tid="report-best" />
            <Card label={`Worst ${periodWord.slice(0, -1)}`} value={s?.worst ? inr(s.worst.pnl) : "—"} sub={s?.worst?.label || "—"} cls="text-bear text-xl" tid="report-worst" />
          </div>

          <div className="panel p-3" data-testid="report-chart">
            <div className="widget-title mb-2">{period === "monthly" ? "Monthly" : "Daily"} P&amp;L ({mode})</div>
            <div className="h-56">
              {chartData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#163056" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10 }} width={62} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                    <Tooltip cursor={{ fill: "#102A4A66" }} contentStyle={{ background: "#0B1F3A", border: "1px solid #163056", fontSize: 11 }} formatter={(v) => inr(v)} labelFormatter={(l) => (data.rows.find((x) => (period === "monthly" ? x.label.replace(" ", "\n") : x.label.slice(0, 6)) === l)?.label) || l} />
                    <Bar dataKey="pnl" radius={[2, 2, 0, 0]} maxBarSize={56}>
                      {chartData.map((x, i) => <Cell key={i} fill={x.pnl >= 0 ? "#22A34A" : "#FF3B30"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-full grid place-items-center text-xs text-slate-500">No closed trades in this range</div>}
            </div>
          </div>

          <div className="panel" data-testid="report-table">
            <div className="px-3 py-2 border-b border-[#163056] widget-title">Breakdown ({data?.rows?.length || 0} {periodWord})</div>
            <div className="max-h-96 overflow-y-auto">
              <table className="tbl w-full">
                <thead><tr><th>{period === "monthly" ? "Month" : "Day"}</th><th>Trades</th><th>Wins</th><th>Losses</th><th>Win %</th><th>Qty</th><th>Best</th><th>Worst</th><th>Net P&amp;L</th><th>Cumulative</th></tr></thead>
                <tbody>
                  {(data?.rows || []).slice().reverse().map((x) => (
                    <tr key={x.period} data-testid={`report-row-${x.period}`}>
                      <td className="text-slate-100">{x.label}</td><td>{x.trades}</td><td className="text-bull">{x.wins}</td><td className="text-bear">{x.losses}</td>
                      <td>{x.win_rate}%</td><td>{x.qty}</td><td className="text-slate-400">{num(x.best_trade, 0)}</td><td className="text-slate-400">{num(x.worst_trade, 0)}</td>
                      <td className={toneCls(x.pnl)}>{inr(x.pnl)}</td><td className={toneCls(x.cum_pnl)}>{inr(x.cum_pnl)}</td>
                    </tr>
                  ))}
                  {!data?.rows?.length && <tr><td colSpan={10} className="text-center text-slate-500 py-4">No closed trades yet — run a paper or live strategy to build your report.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
